import findClosestScrollContainer from "../../utilities/findClosestScrollContainer.js";
import debounce from "../../utilities/debounce.js";

export default (dotnetRef, element, rootId) => {
    if (!element) {
        return {};
    }

    const observedElements = window.ra.intersectionObservers.observedElements;

    const root = window.document.getElementById(rootId) || findClosestScrollContainer(element) || null;
    console.log('[IntersectionObserver] Root: ', root);

    const getCurrentScrollPosition = () => {
        let position = 0;
        if (root) {
            position = root.scrollY || root.scrollTop;
        } else {
            position = window.scrollY || document.documentElement.scrollTop;
        }
        return parseInt(position, 10);
    };

    const show = async (node) => {
        if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (!observedElements[node.id]) {
            observedElements[node.id] = {};
        }

        observedElements[node.id].lastShown = Date.now();

        await dotnetRef.invokeMethodAsync('IntersectionChanged', {
            isIntersecting: true,
            targetId: node.id,
            top: node.offsetTop,
            rootScrollPosition: getCurrentScrollPosition(),
        });
    };

    const hide = async (node) => {
        if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        if (!observedElements[node.id]) {
            observedElements[node.id] = {};
        }

        if (observedElements[node.id].lastShown && Date.now() - observedElements[node.id].lastShown <= 1000) {
            return;
        }

        node.classList.remove('in-viewport');
        node.classList.add('out-viewport');

        observedElements[node.id].height = node.offsetHeight || 50;
        node.style.minHeight = observedElements[node.id].height + 'px';

        await dotnetRef.invokeMethodAsync('IntersectionChanged', {
            isIntersecting: false,
            targetId: node.id,
            top: node.offsetTop,
            rootScrollPosition: getCurrentScrollPosition(),
        });
    };

    const intersectionCallback = async (entries) => {
        for (let i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
                await show(entries[i].target);
            } else {
                await hide(entries[i].target);
            }
        }
    };

    const intersectionObserver = new IntersectionObserver(intersectionCallback, {
        root: root,
        rootMargin: '600px 5000px 600px 5000px',
        threshold: 0.0000001,
    });

    const afterRender = async (node) => {
        node.classList.remove('out-viewport');
        node.classList.add('in-viewport');
        node.style.minHeight = '1rem';
    };

    const observe = async (node) => {
        if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        intersectionObserver.observe(node);
        if (observedElements[node.id]) {
            node.style.minHeight = observedElements[node.id].height + 'px';
        }
    };

    const unobserve = (node) => {
        if (!node || !node.id || node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }

        intersectionObserver.unobserve(node);
    };

    const updateFromScroll = async () => {
        await dotnetRef.invokeMethodAsync('IntersectionChanged', {
            isIntersecting: false,
            targetId: null,
            top: element.offsetTop,
            rootScrollPosition: getCurrentScrollPosition(),
        });
    };
    const scrollHandlerDebounce = debounce(updateFromScroll, 100);
    
    const handleScroll = () => {
        scrollHandlerDebounce();
    };

    // Add scroll listener to the appropriate element
    if (root) {
        root.addEventListener('scroll', handleScroll);
    } else {
        window.addEventListener('scroll', handleScroll);
    }

    const dispose = () => {
        intersectionObserver.disconnect();

        // Remove scroll listener when disposing
        if (root) {
            root.removeEventListener('scroll', handleScroll);
        } else {
            window.removeEventListener('scroll', handleScroll);
        }
    };

    return {
        afterRender,
        observe,
        unobserve,
        dispose,
    };
};
